import { isCabinetPresizedUrl, toImagesWorkerSourceUrl, withImageParams } from 'helpers/imageUrl';

const TWELVE_HOURS = 12;

type AddressLineSource = {
  fullAddress?: string | null;
  street?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
};

// Prefers the complete street+locality address from `fullAddress` (e.g.
// "123 Main St, Phoenix, AZ 85001"), split on the first comma so callers'
// two address rows render a full address. Falls back to structured
// street/city/state/neighborhood fields when `fullAddress` is missing.
export const buildAddressLines = (
  source: AddressLineSource,
): { primaryLine: string | null; secondaryLine: string | null } => {
  const fullAddress = source?.fullAddress?.trim();
  if (fullAddress) {
    const commaIndex = fullAddress.indexOf(',');
    if (commaIndex === -1) {
      return { primaryLine: fullAddress, secondaryLine: null };
    }
    return {
      primaryLine: fullAddress.slice(0, commaIndex).trim(),
      secondaryLine: fullAddress.slice(commaIndex + 1).trim() || null,
    };
  }

  const cityState = [source?.city, source?.state]
    .filter((part): part is string => !!part)
    .join(', ');
  return {
    primaryLine: source?.street ?? source?.neighborhood ?? null,
    secondaryLine: cityState || source?.city || null,
  };
};

// Builds a resized property photo URL via the cosmos image service. Callers
// pass the preset that matches their rendered size to avoid over-fetching.
export const buildPropertyPhotoUrl = (
  photo: string | null | undefined,
  options: { preset: string; presetScale?: string; dpr?: string } = { preset: '3:2-md' },
): string | null => {
  if (!photo) {
    return null;
  }
  const workerSource = toImagesWorkerSourceUrl(photo);
  if (isCabinetPresizedUrl(photo) && !workerSource) {
    return photo;
  }
  // Reuse the canonical param merger so existing query strings / hash fragments
  // on the source URL are preserved instead of being clobbered by a blind `?`.
  return withImageParams(workerSource ?? photo, {
    service: 'cosmos',
    preset: options.preset,
    ...(options.presetScale ? { presetScale: options.presetScale } : {}),
    dpr: options.dpr ?? '1',
  });
};

// Builds the "4 bed · 3 bath · 2,650 sqft" spec line used by Hub cards.
export const buildPropertySpecsLine = (
  beds?: number | null,
  baths?: number | null,
  sqft?: number | null,
): string | null => {
  const parts: string[] = [];
  if (beds != null) {
    parts.push(`${beds} bed`);
  }
  if (baths != null) {
    parts.push(`${baths} bath`);
  }
  if (sqft != null) {
    parts.push(`${sqft.toLocaleString()} sqft`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
};

export const convertDwellingType = (dwellingType?: string | null) => {
  switch (dwellingType) {
    case 'SINGLE_FAMILY':
      return 'Single family';
    case 'TOWNHOUSE':
      return 'Townhouse';
    case 'CONDO':
      return 'Condo';
    case 'MULTI_FAMILY':
      return 'Multi family';
    case 'OTHER':
      return 'Other';
    default:
      return 'Other';
  }
};

const formatHours = (date: Date): string => {
  let hour = date.getHours();
  const isAm = hour < TWELVE_HOURS;
  if (hour > TWELVE_HOURS) {
    hour -= TWELVE_HOURS;
  }
  const openSuffix = isAm ? 'AM' : 'PM';

  return `${hour}${openSuffix}`;
};

export const formatPrettyTimeRange = (start: Date, end: Date): string => {
  const weekday = start.toLocaleDateString([], { weekday: 'short' });
  const month = start.toLocaleDateString([], { month: 'short' });
  const date = start.getDate();
  const startTime = formatHours(start);
  const endTime = formatHours(end);

  return `${weekday} ${month} ${date}, ${startTime} - ${endTime}`;
};
